import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { featureAllowed, isAdminViewer, type PermissionKey } from '../hooks/usePermission';
import { useAuthStore } from '../stores/authStore';
import { useMembershipStore } from '../stores/membershipStore';

/**
 * 会员墙的唯一出口：话术、判定、升级跳转都收在这里。
 *
 * 此前同一句话写在四处（订单页两个分支、客户导出、核算导出与轧差），措辞已经各自漂开，
 * 而且全都是裸 `alert`——说清了「为什么不行」，却没给「那我现在怎么办」。
 * 用户 10-09 裁定：墙只挡导出/分享那一下点击，点下去给的是这句话 **加** 是否升级的跳转。
 */
export const UPGRADE_PATH = '/settings?tab=membership';

/** 统一话术：功能名由调用方给，其余一字不差 */
export const paywallHint = (featureLabel: string): string =>
  `${featureLabel}为尊享会员专属功能，请前往「系统设置 → 会员特权」升级`;

/**
 * 判不过的时候问一句要不要现在去开通。
 * 用 `window.confirm` 而不是自造弹窗组件：这条路径的落点只是一个路由跳转，
 * 弹窗要在每个调用方各挂一份 open 状态，反而更容易漏掉「不升级就什么也不发生」这一支。
 */
function askUpgrade(navigate: ReturnType<typeof useNavigate>, featureLabel: string): void {
  if (window.confirm(`${paywallHint(featureLabel)}\n\n现在去开通？`)) navigate(UPGRADE_PATH);
}

/**
 * 点击那一刻的会员门禁。
 *
 * 为什么不照 `usePermission().can()` 直接判：`/membership/status` 回来前 features 是空数组，
 * 付费用户刚进页面就点导出会被误弹一次墙——墙是弹给人看的，误弹比不弹更糟（人会以为自己掉级了）。
 * 所以状态还没落地时先拉一次再判；服务端那一道 `@RequireFeature` 仍是最终裁判，
 * 它的 403 message 由各调用方的 catch 原样透出，这里不做任何兜底替换。
 */
export function usePaywallGate(): (key: PermissionKey, featureLabel: string) => Promise<boolean> {
  const navigate = useNavigate();

  return useCallback(async (key: PermissionKey, featureLabel: string): Promise<boolean> => {
    const { user, profile } = useAuthStore.getState();
    const isAdmin = isAdminViewer(user, profile);

    const store = useMembershipStore.getState();
    const userId = user?.id ?? null;
    if (!isAdmin && userId && store.status === null) {
      await store.load(userId);
    }

    const features = useMembershipStore.getState().status?.features ?? [];
    if (featureAllowed(isAdmin, features, key)) return true;
    askUpgrade(navigate, featureLabel);
    return false;
  }, [navigate]);
}
